import React, { FC, useCallback } from 'react';
import Card from 'src/components/Card';
import CardTitle from 'src/components/CardTitle';
import { Box, Checkbox, FormControlLabel, Grid } from '@material-ui/core';
import TextWithLabel from 'src/components/TextWithLabel';
import ComponentWithLabel from 'src/components/ComponentWithLabel';
import SiteLogoDropZone from './components/SiteLogoDropZone';
import { Link } from 'react-router-dom';
import { showEntity } from 'src/utils/entityApi';
import useSite from '../../hooks/useSite';
import useAuth from '../../../../../hooks/api/useAuth';

interface Props {
  refreshData: (customerId: number) => void;
  openToast: (success: boolean, message: string) => void;
  openModalCustomerChange: (fieldName: string, value: any) => void;
  customerId: number;
  dataEmployees?: {
    id: number;
    user?: {
      username: string;
    };
  }[];
  dataProFields?: {
    id: number;
    wording: string;
  }[];
  assignedEmployeeInformatiqueId?: number;
  assignedEmployeeTelecomId?: number;
  customerName: string;
  customerProFieldId?: number;
  recoveryTimeGuarantee?: boolean;
  interventionTimeGuarantee?: boolean;
  telecomMaintenanceContract?: boolean;
  computerMaintenanceContract?: boolean;
  hourContract?: boolean;
  customerHeadOffice?: {
    id: number;
    installationSite?: {
      socialReason: string;
    };
  };
  customerCreator?: {
    user: {
      id: number;
      username: string;
    };
  };
  triggerContact?: {
    id: number;
    lastname: string;
    name: string;
  };
}

const CustomerInformationsCard: FC<Props> = ({
  openToast,
  customerId,
  refreshData,
  customerName,
  dataEmployees,
  dataProFields,
  customerCreator,
  customerProFieldId,
  customerHeadOffice,
  assignedEmployeeTelecomId,
  assignedEmployeeInformatiqueId,
  openModalCustomerChange,
  interventionTimeGuarantee,
  recoveryTimeGuarantee,
  hourContract,
  computerMaintenanceContract,
  telecomMaintenanceContract,
  triggerContact,
}) => {
  const { site } = useSite();
  const { fetch: authFetch } = useAuth();

  const handleToggleCheckbox = useCallback(
    (fieldname: string, value: boolean) => {
      const graphQL = `mutation { customer(id: ${customerId}, ${fieldname}: ${value}){id, telecomMaintenanceContract}}`;

      showEntity(authFetch, 'customer', graphQL).then((result) => {
        if (result.wentFine) {
          openToast(true, 'Modification effectuée avec succès !');
          refreshData(customerId);
        } else {
          openToast(false, 'Erreur modification. Veuillez réessayer');
        }
      });
    },
    [customerId, openToast, refreshData]
  );

  const handleCustomerChange = (event: React.ChangeEvent<{ name?: string; value: any }>) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    if (name === 'professionalField') {
      openModalCustomerChange('professionalFieldId', value);
    } else if (name === 'assignedEmployeeTelecom') {
      openModalCustomerChange('assignedEmployeeTelecomId', value);
    } else if (name === 'assignedEmployeeInformatique') {
      openModalCustomerChange('assignedEmployeeInformatiqueId', value);
    }
  };

  return (
    <Box mb='25px' clone>
      <Card>
        <CardTitle title='Information du client' />

        {site.status === 'fetched' && site.data && (
          <Grid item xs={12}>
            <SiteLogoDropZone site={site.data} fullWidth />
          </Grid>
        )}

        <TextWithLabel label='Raison sociale: ' text={customerName} />

        {customerProFieldId && (
          <ComponentWithLabel label='Infos professionelles: '>
            <select
              name='professionalField'
              value={customerProFieldId}
              onChange={handleCustomerChange}
              style={{ fontWeight: 400, marginTop: 7 }}
              className='form-control'
            >
              {dataProFields ? (
                Object.entries(dataProFields).map(([key, data]) => (
                  <option key={key} value={data.id}>
                    {data.wording}
                  </option>
                ))
              ) : (
                <span>Chargement...</span>
              )}
            </select>
          </ComponentWithLabel>
        )}
        {triggerContact && (
          <ComponentWithLabel label='Contact déclencheur: '>
            <Link to={`/dashboard/contact/${triggerContact.id}`}>
              <input
                name='triggerContact'
                value={triggerContact.name + ' ' + triggerContact.lastname.toUpperCase()}
                type='text'
                className='form-control'
                style={{ fontWeight: 400, marginTop: 7 }}
                readOnly
              />
            </Link>
          </ComponentWithLabel>
        )}

        <ComponentWithLabel label='Gestionnaire du compte telecom: '>
          <select
            name='assignedEmployeeTelecom'
            value={assignedEmployeeTelecomId}
            onChange={handleCustomerChange}
            style={{ fontWeight: 400, marginTop: 7 }}
            className='form-control'
          >
            <option value='' />
            {dataEmployees ? (
              Object.entries(dataEmployees).map(([key, data]) => (
                <option key={key} value={data.id}>
                  {data.user?.username}
                </option>
              ))
            ) : (
              <span>Chargement...</span>
            )}
          </select>
        </ComponentWithLabel>

        <ComponentWithLabel label='Gestionnaire du compte informatique: '>
          <select
            name='assignedEmployeeInformatique'
            value={assignedEmployeeInformatiqueId}
            onChange={handleCustomerChange}
            style={{ fontWeight: 400, marginTop: 7 }}
            className='form-control'
          >
            <option value='' />
            {dataEmployees ? (
              Object.entries(dataEmployees).map(([key, data]) => (
                <option key={key} value={data.id}>
                  {data.user?.username}
                </option>
              ))
            ) : (
              <span>Chargement...</span>
            )}
          </select>
        </ComponentWithLabel>

        {customerCreator && (
          <Link to={`/dashboard/user/${customerCreator.user.id}`}>
            <TextWithLabel label='Créateur: ' text={customerCreator.user.username} />
          </Link>
        )}
        {customerHeadOffice && (
          <ComponentWithLabel label='Compte principal: '>
            <Link to={`/dashboard/customer/${customerHeadOffice.id}`}>
              <input
                name='headOffice'
                value={customerHeadOffice.installationSite?.socialReason}
                style={{ fontWeight: 400, marginTop: 7 }}
                type='text'
                className='form-control'
                readOnly
              />
            </Link>
          </ComponentWithLabel>
        )}

        <FormControlLabel
          classes={{ label: 'checkbox-label' }}
          control={
            <Checkbox
              onChange={() => handleToggleCheckbox('telecomMaintenanceContract', !telecomMaintenanceContract)}
              checked={telecomMaintenanceContract}
              color='primary'
            />
          }
          label='Contrat de maintenance télécom'
        />

        <FormControlLabel
          classes={{ label: 'checkbox-label' }}
          control={
            <Checkbox
              onChange={() => handleToggleCheckbox('computerMaintenanceContract', !computerMaintenanceContract)}
              checked={computerMaintenanceContract}
              color='primary'
            />
          }
          label='Contrat de maintenance informatique'
        />

        <FormControlLabel
          classes={{ label: 'checkbox-label' }}
          control={
            <Checkbox
              onChange={() => handleToggleCheckbox('hourContract', !hourContract)}
              checked={hourContract}
              color='primary'
            />
          }
          label='Contrat heure'
        />

        <div>
          <FormControlLabel
            classes={{ label: 'checkbox-label' }}
            control={
              <Checkbox
                onChange={() => handleToggleCheckbox('interventionTimeGuarantee', !interventionTimeGuarantee)}
                checked={interventionTimeGuarantee}
                color='primary'
              />
            }
            label='GTI'
          />

          <FormControlLabel
            classes={{ label: 'checkbox-label' }}
            control={
              <Checkbox
                onChange={() => handleToggleCheckbox('recoveryTimeGuarantee', !recoveryTimeGuarantee)}
                checked={recoveryTimeGuarantee}
                color='primary'
              />
            }
            label='GTR'
          />
        </div>
      </Card>
    </Box>
  );
};

export default CustomerInformationsCard;
